import React from 'react';
import DidEmbed from '../components/DidEmbed';
import { Toaster } from "@/components/ui/sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mic, Sparkles, BookOpen, ArrowLeft, ListChecks, FileText } from 'lucide-react';
import { SessionRecorder } from '../components/SessionRecorder';
import EnhancedCourseOutline from '../components/EnhancedCourseOutline';
import CourseAssignments from '../components/CourseAssignments';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import CourseTutorChat from '../components/CourseTutorChat';

export default function IntroToCS() {
// Removed mocked handleDownloadSummary function as it is replaced by SessionRecorder component

  return (
    <div className="h-screen w-screen bg-slate-50 font-sans text-slate-900 flex flex-col overflow-hidden">
      
      {/* Navbar - Compact */}
      <nav className="bg-white border-b border-slate-200 px-6 py-3 flex-shrink-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto w-full flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/profile">
              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-900">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Profile
              </Button>
            </Link>
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-2">
              <div className="bg-blue-600 p-1.5 rounded-lg shadow-sm">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold text-slate-800">Introduction to Java Programming</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <SessionRecorder courseId="cs101" courseTitle="Introduction to Java Programming" />
            <div className="flex items-center gap-2 px-3 py-1 bg-green-50 border border-green-100 rounded-full text-xs font-medium text-green-700">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              System Online
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content Area - Flex container to fill remaining height */}
      <main className="flex-1 w-full max-w-7xl mx-auto p-4 lg:p-6 grid grid-cols-1 lg:grid-cols-2 gap-6 h-full min-h-0 overflow-hidden">
        
        {/* Left Content Column - Fixed header + scrollable content */}
        <div className="flex flex-col h-full min-h-0">
          {/* Fixed Header Section */}
          <div className="space-y-2 mb-4 flex-shrink-0">
            <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-100 rounded-md px-2.5 py-0.5 w-fit">
              <Sparkles className="w-3 h-3 mr-1.5" /> Virtual Tutor Mode
            </Badge>
            <h1 className="text-2xl lg:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Your AI Teaching <br/>
              <span className="text-blue-600">Assistant is Ready.</span>
            </h1>
            <p className="text-sm lg:text-base text-slate-600 leading-relaxed max-w-lg">
              Enhance your Java programming skills with interactive AI support.
            </p>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 min-h-0 overflow-y-auto pr-2 space-y-3">
            {/* Enhanced Course Outline with Progress Tracking */}
            <div className="max-w-lg w-full">
              <EnhancedCourseOutline
                courseId="cs101"
                courseTitle="Introduction to Java Programming"
                topics={[
                  "Introduction to Java Programming",
                  "Variables, Data Types, and Operators",
                  "Control Flow Statements",
                  "Loops and Iterations",
                  "Introduction to Methods",
                  "Arrays and Strings",
                  "Basic Object-Oriented Concepts",
                  "Classes and Objects"
                ]}
              />
            </div>

            {/* Feature Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg w-full">
              <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-all duration-300">
                <CardHeader className="pb-1.5 pt-3 px-3">
                  <CardTitle className="text-xs font-semibold flex items-center gap-2 text-slate-800">
                    <Mic className="w-3 h-3 text-blue-500" /> Voice Enabled
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3">
                  <p className="text-[10px] text-slate-500 leading-snug">
                    Hands-free interaction. Just speak naturally to ask questions.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-all duration-300">
                <CardHeader className="pb-1.5 pt-3 px-3">
                  <CardTitle className="text-xs font-semibold flex items-center gap-2 text-slate-800">
                    <Sparkles className="w-3 h-3 text-purple-500" /> Smart Responses
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3">
                  <p className="text-[10px] text-slate-500 leading-snug">
                    Powered by advanced AI to provide accurate coding help.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Assignments & Worksheets */}
            <div className="max-w-lg w-full">
              <CourseAssignments courseId="cs101" />
            </div>
          </div>
        </div>

        {/* Right Column - The Avatar Container - Responsive Height */}
        <div className="w-full h-[50vh] lg:h-[85%] relative bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden flex flex-col self-center">
          {/* Header for the Avatar Window */}
          <div className="h-8 bg-slate-50 border-b border-slate-100 flex items-center px-3 gap-2 flex-shrink-0">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400/80"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-amber-400/80"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-green-400/80"></div>
            </div>
            <div className="mx-auto text-[10px] font-medium text-slate-400 uppercase tracking-wide">Your Classroom Assistant is Ready to Help YOU!</div>
          </div>

          {/* The D-ID Avatar Container */}
          <div className="relative flex-1 w-full bg-slate-100 overflow-hidden">
             <DidEmbed agentId="v2_agt_LYpauVih" />
          </div>
        </div>

      </main>
      
      {/* Floating AI Tutor Chat */}
      <CourseTutorChat courseId="cs101" courseName="CS101: Intro to Computer Science" />
      
      <Toaster />
    </div>
  );
}
